
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfilePosts } from "./ProfilePosts";
import { ProfileShares } from "./ProfileShares";
import { ProfilePortfolio } from "./ProfilePortfolio";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, MapPin, Globe, Link } from "lucide-react";

interface SocialLink {
  platform: string;
  url: string;
}

interface Profile {
  id: string;
  username?: string;
  bio?: string;
  location?: string;
  website?: string;
  social_links?: SocialLink[];
  primary_skill?: string;
}

export interface ProfileTabsProps {
  profile: Profile;
  userPosts: any[];
  userShares: any[];
  isMobile: boolean;
  isOwnProfile: boolean;
  galleryItems: any[];
}

export function ProfileTabs({
  profile,
  userPosts,
  userShares,
  isMobile,
  isOwnProfile,
  galleryItems
}: ProfileTabsProps) {
  const [activeTab, setActiveTab] = useState("posts");

  return (
    <Tabs
      defaultValue="posts"
      onValueChange={(value) => setActiveTab(value)}
      className="w-full"
    >
      <TabsList className="grid grid-cols-4 mb-6">
        <TabsTrigger value="posts" className="relative">
          Posts
          {userPosts.length > 0 && (
            <Badge
              variant="secondary"
              className="ml-1.5 h-5 min-w-5 px-1 absolute -top-2 -right-2"
            >
              {userPosts.length}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="shares" className="relative">
          Compartilhados
          {userShares.length > 0 && (
            <Badge
              variant="secondary"
              className="ml-1.5 h-5 min-w-5 px-1 absolute -top-2 -right-2"
            >
              {userShares.length}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="portfolio">Portfólio</TabsTrigger>
        <TabsTrigger value="about">Sobre</TabsTrigger>
      </TabsList>

      <TabsContent value="posts" className="space-y-4">
        <ProfilePosts posts={userPosts} isOwnProfile={isOwnProfile} />
      </TabsContent>

      <TabsContent value="shares" className="space-y-4">
        <ProfileShares shares={userShares} isOwnProfile={isOwnProfile} />
      </TabsContent>

      <TabsContent value="portfolio" className="space-y-4">
        <ProfilePortfolio 
          userId={profile.id} 
          isOwnProfile={isOwnProfile} 
          mediaItems={galleryItems}
          title="Portfolio de Trabalhos"
        />
      </TabsContent>

      <TabsContent value="about" className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Biografia
                </h3>
                <p className="text-muted-foreground">
                  {profile.bio || "Nenhuma biografia adicionada"}
                </p>
              </div>

              {(profile.location || profile.website) && (
                <div className="space-y-4">
                  {profile.location && (
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Localização
                      </h3>
                      <p>{profile.location}</p>
                    </div>
                  )}
                  
                  {profile.website && (
                    <div className="space-y-1">
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <Globe className="h-5 w-5" />
                        Website
                      </h3>
                      <a
                        href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {profile.website}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {profile.social_links && profile.social_links.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Link className="h-5 w-5" />
                    Redes Sociais
                  </h3>
                  <div className="space-y-1">
                    {profile.social_links.map((link, index) => (
                      <a
                        key={index}
                        href={link.url.startsWith("http") ? link.url : `https://${link.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-primary hover:underline"
                      >
                        {link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {profile.primary_skill && (
                <div className="space-y-2">
                  <h3 className="text-lg font-medium">Habilidade Principal</h3>
                  <Badge variant="secondary" className="text-sm font-normal">
                    {profile.primary_skill}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
